import React, { useState, useEffect, useRef } from "react";
import ReactMarkdown from "react-markdown";

const cssStyles = `
  @keyframes blink {
    0%, 50% { opacity: 1; }
    51%, 100% { opacity: 0; }
  }
  
  .chat-message-container {
    scrollbar-width: thin;
    scrollbar-color: rgba(109, 86, 193, 0.6) rgba(15, 23, 42, 0.5);
  }
  
  .chat-message-container::-webkit-scrollbar {
    width: 8px;
  }
  
  .chat-message-container::-webkit-scrollbar-track {
    background: rgba(15, 23, 42, 0.5);
    border-radius: 10px;
  }
  
  .chat-message-container::-webkit-scrollbar-thumb {
    background: rgba(109, 86, 193, 0.6);
    border-radius: 10px;
  }
  
  .chat-message-container::-webkit-scrollbar-thumb:hover {
    background: rgba(109, 86, 193, 0.8);
  }
`;

type Message = {
  role: "user" | "assistant";
  content: string;
};

type ChatProps = {
  compact?: boolean;
};

const Chat = ({ compact = false }: ChatProps) => {
  const storageKey = "vedChatMessages";
  const draftKey = "vedChatDraft";

  // Initialize with stored messages if available
  const getInitialMessages = (): Message[] => {
    try {
      const stored = window.localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Validate that it's an array of messages
        if (Array.isArray(parsed) && parsed.every(msg => msg.role && msg.content)) {
          return parsed;
        } else {
          // Invalid data, clear it
          window.localStorage.removeItem(storageKey);
          return [];
        }
      }
      return [];
    } catch (error) {
      console.warn('Failed to parse stored chat history:', error);
      window.localStorage.removeItem(storageKey);
      return [];
    }
  };

  const getInitialDraft = (): string => {
    try {
      return window.localStorage.getItem(draftKey) || "";
    } catch (error) {
      console.warn('Failed to load draft:', error);
      return "";
    }
  };

  const [messages, setMessages] = useState<Message[]>(getInitialMessages);
  const [input, setInput] = useState(getInitialDraft);
  const [loading, setLoading] = useState(false);
  const [typingMessageId, setTypingMessageId] = useState<string | null>(null);
  const [displayedText, setDisplayedText] = useState<{[key: string]: string}>({});
  const endRef = useRef<HTMLDivElement | null>(null);

  const predefinedQuestions = [
    "What are your main technical skills?",
    "Can you tell me about your AI projects?",
    "What experience do you have with React?",
    "Tell me about your cloud/AWS experience",
    "What are your career goals?",
    "How do you approach problem-solving?"
  ];

  useEffect(() => {
    // Only save messages if we have some and they're valid
    if (messages.length > 0 && messages.every(msg => msg.role && msg.content)) {
      try {
        window.localStorage.setItem(storageKey, JSON.stringify(messages));
      } catch (error) {
        console.warn('Failed to save chat history:', error);
      }
    } else if (messages.length === 0) {
      // Clear localStorage if messages are empty
      window.localStorage.removeItem(storageKey);
    }
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [messages]);

  useEffect(() => {
    // Scroll to bottom when messages are first loaded
    if (messages.length > 0) {
      setTimeout(() => {
        endRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }, 100);
    }
  }, [messages.length]);

  // Typing effect
  useEffect(() => {
    if (typingMessageId && messages.length > 0) {
      const messageIndex = messages.findIndex(msg => msg.role === "assistant");
      if (messageIndex !== -1) {
        const message = messages[messageIndex];
        const fullText = message.content;
        const messageId = `${messageIndex}-assistant`;
        
        if (!displayedText[messageId]) {
          let currentIndex = 0;
          const typeNextChar = () => {
            if (currentIndex < fullText.length) {
              const currentText = fullText.slice(0, currentIndex + 1);
              setDisplayedText(prev => ({
                ...prev,
                [messageId]: currentText
              }));
              currentIndex++;
              
              // Adjust typing speed based on character
              const delay = fullText[currentIndex - 1] === ' ' ? 50 : 
                           ['.', '!', '?', ',', ':'].includes(fullText[currentIndex - 1]) ? 200 : 25;
              
              setTimeout(typeNextChar, delay);
            } else {
              setTypingMessageId(null);
            }
          };
          
          typeNextChar();
        }
      }
    }
  }, [typingMessageId, messages, displayedText]);

  const handleInputChange = (value: string) => {
    setInput(value);
    window.localStorage.setItem(draftKey, value);
  };

  const submitQuestion = async (question: string) => {
    if (!question.trim()) return;

    const userMessage: Message = { role: "user", content: question };

    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    window.localStorage.removeItem(draftKey);
    setLoading(true);

    try {
      const res = await fetch(
        "https://sb6tod243f.execute-api.us-east-1.amazonaws.com/chat",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ question }),
        }
      );

      const data = await res.json();

      const botMessage: Message = {
        role: "assistant",
        content: data.answer || "No response",
      };

      setMessages((prev) => [...prev, botMessage]);
      setTypingMessageId(`${messages.length}-assistant`);
    } catch (err) {
      const errorMessage: Message = {
        role: "assistant", 
        content: "Error occurred"
      };
      setMessages((prev) => [...prev, errorMessage]);
      setTypingMessageId(`${messages.length}-assistant`);
    } finally {
      setLoading(false);
    }
  };

  const clearChatHistory = () => {
    setMessages([]);
    window.localStorage.removeItem(storageKey);
    window.localStorage.removeItem(draftKey);
  };

  // For debugging - you can call this in browser console: window.clearVedChat()
  if (typeof window !== 'undefined') {
    (window as any).clearVedChat = clearChatHistory;
  }

  const chatWrapperStyle = compact ? styles.compactContainer : styles.pageContainer;
  const chatInnerStyle = compact ? styles.compactInner : styles.container;

  return (
    <>
      <style>{cssStyles}</style>
      <div style={chatWrapperStyle}>
      <div style={chatInnerStyle}>
        {!compact && (
          <div style={styles.pageHeader}>
            <div style={styles.pageHeaderContent}>
              <div style={styles.pageHeaderTitle}>
                Ved Prakash AI Agent
              </div>
              <div style={styles.pageHeaderSubtitle}>Ask me anything about Ved's experience, skills, projects, or career!</div>
            </div>
            <div style={styles.pageHeaderActions}>
              <button 
                style={styles.clearButton} 
                onClick={clearChatHistory} 
                title="Clear chat history"
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(255, 255, 255, 0.18)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(255, 255, 255, 0.1)";
                }}
              >
                <i className="fas fa-trash"></i>
              </button>
            </div>
          </div>
        )}
        {/* Messages */}
        <div style={styles.chatBox} className="chat-message-container">
          {messages.length === 0 && !loading && (
            <div style={styles.welcomeSection}>
              <h2 style={styles.welcomeTitle}>👋 Hi! I'm Ved Prakash's AI Assistant</h2>
              <p style={styles.welcomeText}>Ask me anything about Ved's experience, skills, projects, or career!</p>
              <div style={styles.questionsGrid}>
                {predefinedQuestions.map((question, index) => (
                  <button
                    key={index}
                    style={styles.questionButton}
                    onClick={() => submitQuestion(question)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "rgba(109, 86, 193, 0.18)";
                      e.currentTarget.style.borderColor = "rgba(109, 86, 193, 0.45)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "rgba(109, 86, 193, 0.1)";
                      e.currentTarget.style.borderColor = "rgba(109, 86, 193, 0.3)";
                    }}
                  >
                    {question}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg, index) => {
            const messageId = `${index}-${msg.role}`;
            const isTyping = typingMessageId === messageId;
            const displayContent = isTyping ? (displayedText[messageId] || "") : msg.content;
            
            return (
              <div
                key={index}
                style={{
                  ...styles.message,
                  alignSelf:
                    msg.role === "user" ? "flex-end" : "flex-start",
                  background:
                    msg.role === "user" ? "#4f46e5" : "#1f2937",
                }}
              >
                {msg.role === "assistant" ? (
                  <ReactMarkdown>{displayContent}</ReactMarkdown>
                ) : (
                  displayContent
                )}
                {isTyping && <span style={styles.cursor}>|</span>}
              </div>
            );
          })}

          {loading && (
            <div style={{ ...styles.message, background: "#1f2937", alignSelf: "flex-start" }}>
              <div style={styles.thinkingContainer}>
                <div style={styles.thinkingVIcon}>V</div>
                <span>Thinking...</span>
              </div>
            </div>
          )}

          <div ref={endRef} />
        </div>

        {/* Input */}
        <div style={styles.inputBox}>
          <input
            style={styles.input}
            value={input}
            onChange={(e) => handleInputChange(e.target.value)}
            placeholder="Ask Ved..."
            onKeyDown={(e) => e.key === "Enter" && submitQuestion(input)}
          />
          <button style={styles.button} onClick={() => submitQuestion(input)}>
            Send
          </button>
        </div>
      </div>
    </div>
    </>
  );
};

export default Chat;

const styles: { [key: string]: React.CSSProperties } = {
  pageContainer: {
    minHeight: "100vh",
    background: "linear-gradient(135deg, rgba(74, 76, 179, 0.18), rgba(99, 102, 241, 0.12))",
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    padding: "20px",
  },
  container: {
    display: "flex",
    flexDirection: "column",
    height: "85vh",
    width: "100%",
    maxWidth: "1180px",
    background: "rgba(15, 23, 42, 0.96)",
    borderRadius: "24px",
    border: "1px solid rgba(109, 86, 193, 0.22)",
    boxShadow: "0 32px 90px rgba(15, 23, 42, 0.35)",
    overflow: "hidden",
    backdropFilter: "blur(16px)",
  },
  pageHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "20px 24px",
    borderBottom: "1px solid rgba(109, 86, 193, 0.22)",
    background: "linear-gradient(135deg, rgba(74, 76, 179, 0.95), rgba(99, 102, 241, 0.92))",
    borderRadius: "24px 24px 0 0",
  },
  pageHeaderContent: {
    display: "flex",
    flexDirection: "column",
  },
  pageHeaderTitle: {
    fontSize: "18px",
    fontWeight: 700,
    color: "#fff",
  },
  vIcon: {
    width: "32px",
    height: "32px",
    borderRadius: "50%",
    background: "rgba(255, 255, 255, 0.2)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "16px",
    fontWeight: "bold",
    color: "#fff",
  },
  pageHeaderSubtitle: {
    fontSize: "14px",
    color: "rgba(255, 255, 255, 0.78)",
    marginTop: "4px",
  },
  pageHeaderActions: {
    display: "flex",
    alignItems: "center",
  },
  clearButton: {
    width: "36px",
    height: "36px",
    border: "none",
    borderRadius: "999px",
    background: "rgba(255, 255, 255, 0.1)",
    color: "#fff",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "14px",
    transition: "all 0.2s ease",
  },
  compactContainer: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    height: "100%",
    background: "transparent",
    overflow: "hidden",
  },
  compactInner: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    height: "100%",
    background: "rgba(15, 23, 42, 0.96)",
    overflow: "hidden",
  },
  chatBox: {
    flex: 1,
    overflowY: "auto",
    padding: "22px",
    paddingBottom: "32px",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    background: "rgba(15, 23, 42, 0.82)",
    minHeight: 0,
  },
  welcomeSection: {
    textAlign: "center",
    padding: "40px 20px",
    color: "white",
    background: "rgba(15, 23, 42, 0.55)",
    borderRadius: "20px",
    border: "1px solid rgba(109, 86, 193, 0.2)",
  },
  welcomeTitle: {
    fontSize: "24px",
    fontWeight: "bold",
    marginBottom: "12px",
    color: "#4f46e5",
  },
  welcomeText: {
    fontSize: "15px",
    marginBottom: "24px",
    color: "#d1d5db",
    maxWidth: "720px",
    marginLeft: "auto",
    marginRight: "auto",
  },
  questionsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: "10px",
    maxWidth: "720px",
    margin: "0 auto",
  },
  questionButton: {
    display: "flex",
    justifyContent: "flex-start",
    padding: "12px 16px",
    background: "rgba(109, 86, 193, 0.1)",
    border: "1px solid rgba(109, 86, 193, 0.35)",
    borderRadius: "14px",
    color: "#ede9fe",
    cursor: "pointer",
    fontSize: "13px",
    textAlign: "left",
    transition: "all 0.2s ease",
    fontFamily: "inherit",
    minHeight: "56px",
    lineHeight: "1.5",
  },
  message: {
    maxWidth: "84%",
    padding: "14px 16px",
    borderRadius: "18px",
    fontSize: "14px",
    lineHeight: "1.7",
    wordBreak: "break-word",
    color: "#e5e7eb",
  },
  thinkingContainer: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  thinkingVIcon: {
    width: "20px",
    height: "20px",
    borderRadius: "50%",
    background: "rgba(109, 86, 193, 0.3)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "10px",
    fontWeight: "bold",
    color: "#c7d2fe",
    flexShrink: 0,
  },
  inputBox: {
    display: "flex",
    gap: "12px",
    padding: "18px 20px",
    borderTop: "1px solid rgba(109, 86, 193, 0.22)",
    background: "rgba(15, 23, 42, 0.9)",
    position: "sticky",
    bottom: 0,
    zIndex: 10,
  },
  input: {
    flex: 1,
    padding: "14px 16px",
    borderRadius: "999px",
    border: "1px solid rgba(109, 86, 193, 0.4)",
    outline: "none",
    fontSize: "14px",
    background: "rgba(18, 33, 63, 0.95)",
    color: "#e5e7eb",
    fontFamily: "inherit",
  },
  button: {
    marginLeft: "12px",
    padding: "12px 20px",
    borderRadius: "999px",
    border: "none",
    background: "#6d56c1",
    color: "white",
    cursor: "pointer",
    fontWeight: 600,
    fontFamily: "inherit",
    boxShadow: "0 12px 25px rgba(109, 86, 193, 0.24)",
  },
  cursor: {
    color: "#6d56c1",
    animation: "blink 1s infinite",
    fontWeight: "bold",
  }
};